import { LIMIT, LIMIT_MAX, PAGE_PATH } from '@/constants';
import { fetchSubscriptionsList } from '@/services/apis';
import {
  fetchSubscriptionCompRecordsDocument,
  fetchSubscriptionOperationsDocument,
} from '@/services/graphql/docs';
import { fetcher } from '@/services/graphql/fetcher/index';
import {
  GiftState,
  LoadMoreStatus,
  Sort,
  SubscriptionState,
  type AnyObj,
  type AnyObjOrNull,
} from '@/types';
import { commonUtils } from '@/utils';
import { defineStore } from 'pinia';
import {
  fetchSubscriptionDetails,
  fetchUpdateSubscriptionAddress,
} from './../services/apis/subscribe';
import { useAddressStore } from './address';

interface State {
  /** 订单列表 */
  subscribeList: AnyObj[];
  /** 列表请求选项 */
  options: { filterState: AnyObj; skip: number };
  /** 列表加载状态 */
  loadStatus: LoadMoreStatus;
  /** 标签栏下标 */
  tabIndex: number;
  /** 订阅详情数据 */
  subscribeDetails: AnyObjOrNull;
  /** 订阅关联礼品数据 */
  subscriptionCompRecords: AnyObj[];
  /** 订阅退款操作信息 */
  operations: AnyObjOrNull;
}

export const useSubscribeStore = defineStore('subscribe', {
  state: (): State => ({
    subscribeList: [],
    options: {
      filterState: {
        // contains: '',
      },
      skip: 0,
    },
    loadStatus: LoadMoreStatus.loading,
    tabIndex: 0,
    subscribeDetails: null,
    subscriptionCompRecords: [],
    operations: null,
  }),

  getters: {
    /** 是否已关闭 */
    isCancel(state) {
      return state.subscribeDetails?.state === SubscriptionState.cancel;
    },
    /** 是否多期模式 */
    isMultiple(state) {
      return state.subscribeDetails?.expireNumberOfPeriod > 1;
    },
    /** 可领取的数量 */
    availableCount(state) {
      return state.subscriptionCompRecords.length;
    },
    /** 当前最新的礼品数据信息 */
    newestGiftInfo(state) {
      return state.subscriptionCompRecords?.[0];
    },
  },

  actions: {
    async getSubscribeList(skip: number = 0, refresh?: boolean) {
      const res = await fetchSubscriptionsList(skip, this.options.filterState);
      if (res) {
        if (res.length < LIMIT) {
          this.loadStatus = LoadMoreStatus.noMore;
        } else {
          this.loadStatus = LoadMoreStatus.more;
        }
        if (refresh) {
          this.subscribeList = res;
        } else {
          this.subscribeList = [...this.subscribeList, ...res];
        }
      }
      return this.subscribeList;
    },
    /**
     * 获取订阅退款操作信息
     * @param subscriptionId - 订阅id
     */
    async fetchOperations(subscriptionId: string) {
      try {
        const res = await fetcher.query(fetchSubscriptionOperationsDocument, {
          variables: { subscriptionId, operationType: 'unsubscribe' },
        });
        if (res.data) {
          this.operations = res.data.value.subscriptionOperations?.[0];
        }
      } catch (error) {
        console.log(error);
      }
    },
    /**
     * 获取订单关联的礼品信息
     * @param subscriptionId - 订阅id
     */
    async fetchCompRecords(subscriptionId: string) {
      try {
        const res = await fetcher.query(fetchSubscriptionCompRecordsDocument, {
          variables: {
            subscriptionId,
            orderId: '',
            options: {
              take: LIMIT_MAX,
              sort: {
                createdAt: Sort.DESC,
              },
              filter: {
                state: {
                  in: [GiftState.created, GiftState.draw],
                },
              },
            },
          },
        });
        if (res.data) {
          this.subscriptionCompRecords = res.data.value?.subscriptionCompRecords?.items;
        }
      } catch (error) {
        console.log(error);
      }
    },
    async getSubscriptionDetails(orderId: string, refresh?: boolean) {
      if (!this.subscribeDetails || refresh) {
        this.subscribeDetails = await fetchSubscriptionDetails(orderId);
      }
      return this.subscribeDetails;
    },
    async getOperations(id: string) {
      if (commonUtils.func.isEmpty(this.operations)) {
        await this.fetchOperations(id);
      }
      return this.operations;
    },
    async getCompRecords(subscriptionId: string = '') {
      if (commonUtils.func.isEmpty(this.subscriptionCompRecords)) {
        await this.fetchCompRecords(subscriptionId);
      }
      return this.subscriptionCompRecords;
    },
    async modifyAddressHandler(data: AnyObjOrNull) {
      if (!data) return;

      const addressStore = useAddressStore();

      const subscriptionId = data.id;

      addressStore.displayedAddress = data.shippingAddress;

      addressStore.selectedAfterCallback = async (selected: AnyObj) => {
        await fetchUpdateSubscriptionAddress(selected, subscriptionId);

        // 更新列表数据和详情数据
        const detail = await this.getSubscriptionDetails(subscriptionId, true);
        const index = this.subscribeList.findIndex((it) => it.id === subscriptionId);
        if (index >= 0 && this.subscribeList && detail) {
          this.subscribeList[index] = detail;
        }

        addressStore.selectedAfterCallback = null;
      };

      uni.navigateTo({ url: PAGE_PATH.addressModify.route });
    },
  },
});
